<template>
  <layout>
    <bb-container fluid>
      <h4 class="f-gotham-medium f-color-navy">{{ translations.title }}</h4>
      <div v-if="!representative" class="loader">
        <bb-spinner/>
      </div>
      <div v-if="representative">
        <bb-card size="full" class="m-t-20">
          <div class="d-flex justify-content-start">
            <bb-label
              v-if="representative.isUserManager"
              class="bg-mint"
              size="18"
            >
              {{ translations.superUser }}
            </bb-label>
            <h5 class="f-gotham-medium f-color-navy f-small m-l-10">{{ representative.fullName }} •
              {{ representative.identificationCode }}</h5>
          </div>
          <div class="d-flex gap-50 m-t-20">
            <bb-labeled-value
              :value="formatToTwoDigitsOrEmpty(representative.effectiveFrom)"
              size="18"
              label-weight="bold"
              font-weight="bold"
              :label="translations.validFrom"
              label-uppercase
            />
            <bb-labeled-value
              :value="formatToTwoDigitsOrEmpty(representative.effectiveTo) || '-'"
              size="18"
              label-weight="bold"
              font-weight="bold"
              :label="translations.validThru"
              label-uppercase
              class="m-l-30"
            />
          </div>
          <h5 class="f-gotham-medium f-color-navy f-small m-y-20">{{ translations.companyDataAndDocuments }}:</h5>
          <div class="row row-gap-5 f-gotham-book f-small">
            <div
              class="col-12 d-flex align-items-center"
              v-for="representativePermission in groupedRepresentativePermissions[PermissionGroup.CustomerData]"
              :key="representativePermission.title"
            >
              <bb-icon
                :name="representativePermission.hasPermission ? 'ui-check' : 'ui-close'"
                size="24"
                :fill="representativePermission.hasPermission ? 'mint' : 'red'"
                class="m-r-15"
              />
              <div class="f-gotham-small f-color-navy f-small">
                {{ representativePermission.title }}
              </div>
            </div>
          </div>
          <bb-tabs
            :tabs="productPermissionTabs"
            v-model="selectedPermissionGroup"
            class="m-t-30"
          />
          <div class="m-t-20">
            <div
              v-for="(permissions, group) in groupedRepresentativePermissions"
              :key="group"
              v-show="group === selectedPermissionGroup"
            >
              <div
                class="d-flex m-t-15 f-gotham-book f-small"
                v-for="representativePermission in permissions"
                :key="representativePermission.title"
              >
                <bb-icon
                  :name="representativePermission.hasPermission ? 'ui-check' : 'ui-close'"
                  size="24"
                  :fill="representativePermission.hasPermission ? 'mint' : 'red'"
                  class="m-r-15"
                />
                <div class="f-gotham-small f-color-navy f-small">
                  {{ representativePermission.title }}
                </div>
              </div>
            </div>
          </div>
        </bb-card>
      </div>
    </bb-container>
  </layout>
</template>

<script>
import Layout from './Layout'
import '@bigbank/interface-components/dist/svg/client/pdf'
import '@bigbank/interface-components/dist/svg/ui/download'
import '@bigbank/interface-components/dist/svg/client/upload'
import { BbLabel, BbLabeledValue, BbTabs } from '@bigbank/interface-components'
import { formatToTwoDigitsOrEmpty } from '@/plugins/dateFormatters'
import accountApi from '@account/api'
import { permissionGroupTranslations, permissionTranslations } from '@account/translations/permissionTranslations'
import {
  PermissionGroup,
  permissionGroups
} from '@bigbank/dc-common/clients/http/access-management/access-management.enums'

const genericPermissionGroups = [PermissionGroup.CustomerData]
const hiddenPermissionGroups = [PermissionGroup.Login, PermissionGroup.Admin]

export default {
  name: 'customer-users',
  components: { BbLabel, Layout, BbTabs, BbLabeledValue },
  data () {
    return {
      representative: null,
      selectedPermissionGroup: null,
      groupedRepresentativePermissions: null
    }
  },
  computed: {
    PermissionGroup () {
      return PermissionGroup
    },
    translations () {
      return {
        title: this.$pgettext('customer_users', 'Users & transfer limits'),
        companyDataAndDocuments: this.$pgettext('customer_users', 'Company data and documents'),
        validFrom: this.$pgettext('customer_users', 'Valid From'),
        validThru: this.$pgettext('customer_users', 'Valid Thru'),
        superUser: this.$pgettext('customer_users', 'Superuser'),
        permissions: permissionTranslations(),
        permissionGroups: permissionGroupTranslations()
      }
    },
    productPermissionGroups () {
      return Object.values(PermissionGroup)
        .filter(group => !genericPermissionGroups.includes(group) && !hiddenPermissionGroups.includes(group))
    },
    productPermissionTabs () {
      return this.productPermissionGroups
        .map(group => ({
          id: group,
          title: this.translations.permissionGroups[group]
        }))
    }
  },
  methods: {
    groupPermissions (permissions) {
      const groupedRepresentativePermissionsResult = {}
      Object.entries(permissionGroups).forEach(([group, groupPermissions]) => {
        groupedRepresentativePermissionsResult[group] = groupPermissions.map(groupPermission => ({
          title: this.translations.permissions[groupPermission],
          hasPermission: permissions.includes(groupPermission)
        }))
      })
      return groupedRepresentativePermissionsResult
    },
    formatToTwoDigitsOrEmpty
  },
  async mounted () {
    this.representative = await accountApi.getRepresentative()
    this.groupedRepresentativePermissions = this.groupPermissions(this.representative.permissions)
    this.selectedPermissionGroup = this.productPermissionGroups?.length > 0
      ? this.productPermissionGroups[0]
      : undefined
  }
}
</script>
